import type { AuthoredDesign } from "@keydials/api-client";
import { builderCopy as copy } from "./copy";

// ---------------------------------------------------------------------------
// Being built (KEY-560): the shelf on My Dials of the dials this person is building. Each card says where its dial is (being
// built, a test copy, or running) and offers the one way back into it. Only a dial that's still being built can be discarded
// here: a test copy is put away from Launch or the test copy itself, and a running dial belongs to the people using it.
// ---------------------------------------------------------------------------

export interface ShelfCard {
  id: string;
  name: string;
  state: AuthoredDesign["state"];
  stateLabel: string;
  action: { label: string; to: string };
  canDiscard: boolean;
}

export type ShelfLoad = { kind: "loading" } | { kind: "failed" } | { kind: "loaded"; designs: readonly AuthoredDesign[] };

export type DiscardResult = { kind: "discarded" } | { kind: "refused"; message: string } | { kind: "gone" } | { kind: "failed" };

const ORDER: Record<AuthoredDesign["state"], number> = { Building: 0, TestCopy: 1, Running: 2 };

function actionFor(design: AuthoredDesign): ShelfCard["action"] {
  // A test copy or running dial whose id didn't come back opens at the builder's Launch stage, which finds its way from there.
  const launch = { label: copy.waysIn.carryOn, to: `/build/${design.id}?stage=launch` };
  switch (design.state) {
    case "TestCopy":
      return design.checkingInstanceId ? { label: copy.launch.openTestCopy, to: `/instances/${design.checkingInstanceId}` } : launch;
    case "Running":
      return design.liveInstanceId ? { label: copy.launch.openDial, to: `/instances/${design.liveInstanceId}` } : launch;
    default:
      return { label: copy.waysIn.carryOn, to: `/build/${design.id}` };
  }
}

/** The shelf's cards: dials still being built first, then test copies, then running dials, each by name. */
export function shelfCards(designs: readonly AuthoredDesign[]): ShelfCard[] {
  return [...designs]
    .sort((a, b) => (ORDER[a.state] ?? 3) - (ORDER[b.state] ?? 3) || a.name.localeCompare(b.name))
    .map((design) => ({
      id: design.id,
      name: design.name.trim() || copy.frame.untitled,
      state: design.state,
      stateLabel: copy.waysIn.states[design.state] ?? design.state,
      action: actionFor(design),
      canDiscard: design.state === "Building",
    }));
}

/**
 * What the shelf shows, or null for no shelf: not while the list loads, not when it couldn't be read (My Dials is the page everyone
 * lands on, so it doesn't get an error about a shelf), and not when nothing is being built.
 */
export function shelfView(load: ShelfLoad): ShelfCard[] | null {
  if (load.kind !== "loaded" || load.designs.length === 0) return null;
  return shelfCards(load.designs);
}

const statusOf = (error: unknown): number | null => {
  const status = (error as { status?: unknown } | null)?.status;
  return typeof status === "number" ? status : null;
};

/** Discards a dial still being built. One that's already gone counts as discarded; a refusal keeps the server's sentence. */
export async function runDiscard(remove: () => Promise<void>): Promise<DiscardResult> {
  try {
    await remove();
    return { kind: "discarded" };
  } catch (error) {
    const status = statusOf(error);
    if (status === 404) return { kind: "gone" };
    if (status === 409 || status === 400) return { kind: "refused", message: error instanceof Error ? error.message : "" };
    return { kind: "failed" };
  }
}
